'use client';

import React, { useEffect, useState } from 'react';
import BaseMechanicShell from './BaseMechanicShell';
import { ListOrdered } from 'lucide-react';

interface SequenceBuilderMechanicProps {
  lessonSlug: string;
  mechanicId?: string;
  id?: string;
  prompt?: string;
  tier?: string;
  localHints?: { id: string; hint: string }[];
  localValidator?: { kind?: string; correctSequence?: string[]; hints?: any[] };
  payload: {
    items: Array<{ id: string; content: string }>;
    _hasHints?: boolean;
  };
}

export default function SequenceBuilderMechanic({ lessonSlug, mechanicId, id, prompt, tier, localHints, localValidator, payload }: any) {
  const actualMechanicId = mechanicId || id || '';
  return (
    <BaseMechanicShell
      lessonSlug={lessonSlug}
      mechanicId={actualMechanicId}
      prompt={prompt || ''}
      icon={<ListOrdered className="w-5 h-5" />}
      iconLabel="Sequence Builder — Arrange items in the correct order"
      payload={payload}
      tier={tier}
      localHints={localHints}
      localValidator={localValidator}
    >
      {(submissionState, setAnswer) => (
        <SequenceBuilderMechanicInner payload={payload} submissionState={submissionState} setAnswer={setAnswer} />
      )}
    </BaseMechanicShell>
  );
}


function SequenceBuilderMechanicInner({ payload, submissionState, setAnswer }: any) {
  const [sequence, setSequence] = useState<string[]>([]);
  const [shuffledItems, setShuffledItems] = useState<any[]>([]);

  useEffect(() => {
    if (payload?.items && shuffledItems.length === 0) {
      // Deterministic pseudo-random shuffle to prevent hydration mismatch, or just simple random on mount
      const arr = [...payload.items];
      for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
      }
      setShuffledItems(arr);
    }
  // shuffledItems.length is intentionally included: once populated (length > 0) the
  // condition exits early, so there's no infinite loop.
  }, [payload?.items, shuffledItems.length]);

  const items = shuffledItems.length > 0 ? shuffledItems : payload?.items || [];

  useEffect(() => {
    if (sequence.length > 0) {
      setAnswer(sequence);
    } else {
      setAnswer(null);
    }
  }, [sequence, setAnswer]);

  const toggleItem = (id: string) => {
    if (submissionState !== 'idle' && submissionState !== 'error') return;
    if (sequence.includes(id)) {
      setSequence(sequence.filter(x => x !== id));
    } else {
      setSequence([...sequence, id]);
    }
  };

  return (
    <div className="flex flex-col gap-8 w-full mt-4">
      {/* Sequence Track (Top) */}
      <div className="flex flex-col gap-3">
        <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-widest ml-2">Target Sequence</h4>
        <div className="bg-muted/10 border-2 border-border/50 rounded-2xl p-6 min-h-[140px] shadow-inner flex flex-col gap-3 relative">
          
          {sequence.length === 0 ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-sm text-muted-foreground/60 font-semibold uppercase tracking-widest italic">Sequence Empty</span>
            </div>
          ) : (
            <div className="flex flex-col gap-2 z-10 relative">
              {sequence.map((id, index) => {
                const item = items.find((i: any) => i.id === id);
                const isLast = index === sequence.length - 1;
                return (
                  <div key={id} className="relative group">
                    <div 
                      onClick={() => toggleItem(id)} 
                      className="px-4 py-3 bg-primary text-primary-foreground text-sm font-medium rounded-xl cursor-pointer hover:bg-destructive hover:text-destructive-foreground transition-all duration-300 flex items-center gap-4 shadow-md group-hover:-translate-x-1"
                    >
                      <div className="w-6 h-6 rounded-full bg-background/20 flex items-center justify-center shrink-0">
                        <span className="text-xs font-bold">{index + 1}</span>
                      </div>
                      <span className="flex-1">{item?.content || id}</span>
                    </div>
                    {/* Connection Line */}
                    {!isLast && (
                      <div className="absolute left-7 top-full w-0.5 h-3 bg-primary/30 z-0"></div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Available Pool (Bottom) */}
      <div className="flex flex-col gap-3">
        <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-widest ml-2">Data Pool</h4>
        <div className="bg-background border-2 border-border/80 shadow-md rounded-2xl p-6">
          <div className="flex flex-wrap gap-3">
            {items.filter((i: any) => !sequence.includes(i.id)).map((item: any) => (
              <div
                key={item.id}
                onClick={() => toggleItem(item.id)}
                className="px-4 py-3 bg-muted/30 border-2 border-border/80 hover:border-primary text-foreground text-sm font-semibold rounded-xl cursor-pointer transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 select-none flex items-center gap-2"
              >
                <div className="w-2 h-2 rounded-full bg-primary/50"></div>
                {item.content}
              </div>
            ))}
            
            {items.length > 0 && items.every((i: any) => sequence.includes(i.id)) && (
               <div className="w-full text-center py-4">
                 <span className="text-sm text-primary font-bold">All items slotted!</span>
               </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}